import {
  SubNavigationBar,
  SubNavigationItem,
} from '@/modules/shared/components'
import * as React from 'react'
import { useLocation, useMatch } from 'react-router-dom'
import { DRIVER_ROUTES } from '../routes-typed'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useIsDriverOnly } from '@/modules/shared/permissions'

interface NavigationProps {
  companyId: string
  selectedDriverId?: number
}

const Navigation: React.FC<NavigationProps> = ({
  companyId,
  selectedDriverId,
}) => {
  const { t } = useLocalize()
  const { isLoadingBasicDrivers } = useDrivers()
  // Kierowca ma tylko aktywności i naruszenia; pozostałe zakładki serwer mu odrzuca.
  const isDriverOnly = useIsDriverOnly(companyId)
  const { getDateRangeForView } = useDate()
  const cid = companyId

  // Detect the single-driver vs group view from the URL shape. If the URL
  // carries `/driver/:driverId`, we're on a single-driver page — match via
  // useMatch against any of the "single" route patterns so we don't hand-roll
  // a regex.
  const singleContractsMatch = useMatch(`${DRIVER_ROUTES.contracts.path}/*`)
  const singleActivitiesMatch = useMatch(`${DRIVER_ROUTES.activities.path}/*`)
  const singleTimesheetMatch = useMatch(`${DRIVER_ROUTES.timesheet.path}/*`)
  const singleBusinessTripsMatch = useMatch(
    `${DRIVER_ROUTES.businessTrips.path}/*`,
  )
  const singleScheduleMatch = useMatch(`${DRIVER_ROUTES.schedule.path}/*`)
  const { pathname } = useLocation()
  const isOnSingleDriverRoute =
    !!(singleContractsMatch ||
      singleActivitiesMatch ||
      singleTimesheetMatch ||
      singleBusinessTripsMatch ||
      singleScheduleMatch) ||
    // Fallback for sibling single-driver routes without a typed entry
    /\/driver\/\d+/.test(pathname)

  const driversNotReady = isLoadingBasicDrivers
  const shouldUseSingleDriverView = isOnSingleDriverRoute && selectedDriverId

  const hasDriver = !!selectedDriverId && selectedDriverId > 0

  return (
    <SubNavigationBar>
      {!isDriverOnly && (
        <SubNavigationItem
          to={
            shouldUseSingleDriverView
              ? DRIVER_ROUTES.contracts.buildPath({
                  companyId: cid,
                  driverId: selectedDriverId!,
                })
              : DRIVER_ROUTES.groupContracts.buildPath({ companyId: cid })
          }
          matchSubRoutes
        >
          {t('tab.basic-data')} / {t('tab.contracts')}
        </SubNavigationItem>
      )}

      <SubNavigationItem
        // Activities requires a concrete driver. If none is selected, disable
        // the tab instead of producing `/driver/undefined/...` like the legacy
        // generatePath would have.
        to={
          hasDriver
            ? DRIVER_ROUTES.activities.buildPath({
                companyId: cid,
                driverId: selectedDriverId!,
              })
            : ''
        }
        matchSubRoutes
        disabled={driversNotReady || !hasDriver}
      >
        {t('tab.activities')}
      </SubNavigationItem>

      {!isDriverOnly && (
        <SubNavigationItem
          to={
            shouldUseSingleDriverView
              ? DRIVER_ROUTES.timesheet.buildPath({
                  companyId: cid,
                  driverId: selectedDriverId!,
                })
              : DRIVER_ROUTES.groupTimesheet.buildPath({ companyId: cid })
          }
          matchSubRoutes
          disabled={driversNotReady}
        >
          {t('tab.timesheet')}
        </SubNavigationItem>
      )}

      {!isDriverOnly && (
        <SubNavigationItem
          to={
            shouldUseSingleDriverView
              ? DRIVER_ROUTES.businessTrips.buildPath({
                  companyId: cid,
                  driverId: selectedDriverId!,
                  ...getDateRangeForView(),
                })
              : DRIVER_ROUTES.groupBusinessTrips.buildPath({
                  companyId: cid,
                  ...getDateRangeForView(),
                })
          }
          matchSubRoutes
          disabled={driversNotReady}
          // Custom active-match: businessTrips URLs differ only in the date
          // range tail. Treat any /businessTrip/... or /business-trips/... URL
          // under this company as active regardless of which dates are in it.
          isActive={(currentPath) =>
            shouldUseSingleDriverView
              ? new RegExp(
                  `/et/company/${cid}/driverModule/driver/\\d+/businessTrip/`,
                ).test(currentPath)
              : new RegExp(
                  `/et/company/${cid}/driverModule/business-trips/`,
                ).test(currentPath)
          }
        >
          {t('tab.delegations')}
        </SubNavigationItem>
      )}

      <SubNavigationItem
        to={
          shouldUseSingleDriverView
            ? DRIVER_ROUTES.violationsNew.buildPath({
                companyId: cid,
                driverId: selectedDriverId!,
                ...getDateRangeForView(),
              })
            : // Group "violations-new" route with no date range in URL;
              // the page will hydrate a default from the date store.
              `/et/company/${cid}/driverModule/violations-new`
        }
        matchSubRoutes
        disabled={driversNotReady}
      >
        {t('tab.violations')}
      </SubNavigationItem>
    </SubNavigationBar>
  )
}

export default Navigation
